(ns practitest.frontend.components2.dashboard.charts.marks
  (:require [practitest.frontend.components2.dashboard.charts.marks-gen :as gen]
            [practitest.frontend.components2.dashboard.charts.marks-bar :as bar]
            [practitest.frontend.components2.dashboard.charts.marks-gauge :as gauge]
            [practitest.frontend.components2.dashboard.charts.markers :as markers]
            [practitest.frontend.components2.dashboard.charts.arc-labels :as arc-labels]
            ["d3" :as d3]))

(defmulti render!
  (fn [_svg-g mark _spec] (:type mark)))

(def ^:private min-arc-angle
  "Smallest angle (radians, ~3°) a non-zero pie slice is drawn with, so tiny
   slices stay visible and hoverable."
  0.07)

(defn- grouping-field [encode]
  (or (get-in encode [:color :field])
      (get-in encode [:detail :field])))

(defn- sorting-field [encode]
  (or (get-in encode [:order :field])
      (get-in encode [:x :field])))

(def AreaRows
  [:vector [:map-of :string [:or inst? number?]]])

(defn prepare-area-rows
  {:malli/schema [:=> [:cat
                       [:sequential inst?]
                       [:map-of inst? [:vector [:map
                                                [:date  inst?]
                                                [:group :string]
                                                [:value number?]]]]
                       :keyword]
                  AreaRows]}
  [categories by-category x-field]
  (mapv (fn [cat]
          (let [rows-for-cat (get by-category cat [])
                base-row     {(name x-field) cat}]
            (reduce (fn [obj {:keys [group value]}]
                      (assoc obj (str group) value))
                    base-row
                    rows-for-cat)))
        categories))

(defn- prepare-data [encode data]
  (let [grp-field (grouping-field encode)
        srt-field (sorting-field encode)]
    (-> data
        (cond->>
            (vector? data) (group-by grp-field))
        (update-vals #(vec (sort-by srt-field %))))))

(defn- line-layer
  "Builds a plain JS array of per-category values for one line series, so it
   can be walked by index the same way markers/render! walks stacked layers."
  [cat-index x-field value-field {:keys [key points]}]
  (let [values (reduce (fn [acc pt]
                          (if-let [i (get cat-index (.getTime (get pt x-field)))]
                            (assoc acc i (get pt value-field))
                            acc))
                        (vec (repeat (count cat-index) nil))
                        points)
        arr    (clj->js values)]
    (set! (.-key arr) key)
    arr))

(defn- animation-defaults [mark]
  (merge {:ease-exit       (.-easeCubicIn d3)
          :ease-update     (.-easeCubicInOut d3)
          :ease-draw       (.-easeCubicOut d3)
          :ease-enter      (.-easeCubicOut d3)
          :exit-duration   400
          :update-duration 600
          :draw-duration   1800
          :enter-duration  400}
         (:animation mark)))

(defn- trace-in!
  "Reveals a freshly-entered line path by animating stroke-dashoffset from
   the path's full length down to 0, so it appears to draw itself in."
  [sel duration ease]
  (-> sel
      (.attr "stroke-dasharray" (fn [_d] (this-as this (.getTotalLength this))))
      (.attr "stroke-dashoffset" (fn [_d] (this-as this (.getTotalLength this))))
      (.transition "line-trace")
      (.duration duration)
      (.ease ease)
      (.attr "stroke-dashoffset" 0)
      (.on "end" (fn [_d] (this-as this (.setAttribute this "stroke-dasharray" "none"))))))

(defn- arc-centroid
  "[x y] at distance r from center, along d's slice mid-angle — used to nudge
   a slice outward on hover without hand-computing trig."
  [^js arc-at-r ^js d]
  (let [c (.centroid arc-at-r d)]
    [(aget c 0) (aget c 1)]))

(defn- hover-move!
  "mouseenter/mouseleave handler for a slice's invisible hit-path: moves its
   sibling .mark-arc via a CSS transform and resets it back to place on leave."
  [^js hover-arc hover?]
  (fn [^js _event ^js d]
    (this-as this
      (let [group     (.select d3 (.-parentNode this))
            transform (if hover?
                        (let [[dx dy] (arc-centroid hover-arc d)]
                          (str "translate(" dx "px," dy "px)"))
                        "translate(0,0)")]
        (-> group (.select "path.mark-arc") (.style "transform" transform))))))

(defn- interp-tween
  "Generic attrTween scaffold: stashes the node's last-rendered datum in
   `_current`, interpolates from it to the new datum, and calls `apply-fn`
   with each intermediate value."
  [apply-fn]
  (fn [^js d]
    (this-as ^js this
      (let [current (or (.-_current this) d)
            interp  (.interpolate d3 current d)]
        (set! (.-_current this) (interp 0))
        (fn [t] (apply-fn (interp t)))))))

(defn- arc-tween [arc-gen]
  (interp-tween arc-gen))

(defn- collapsed-arc [^js d]
  (let [c (js/Object.assign #js {} d)]
    (set! (.-startAngle c) (.-endAngle c))
    c))

(defn- sweep-arc!
  "Runs sel's transition and tweens its 'd' attr through arc-gen from
   whatever shape the node last rendered."
  [sel duration ease arc-gen]
  (-> sel
      (.transition) (.duration duration) (.ease ease)
      (.attrTween "d" (arc-tween arc-gen))))

(defn- arc-tooltip-payload
  [marks-g label-positions cat-key center-x center-y ^js d]
  (let [[lx ly] (arc-labels/label-anchor marks-g label-positions d)]
    {:category       (cat-key d)
     :rows           []
     :anchor-x       (+ center-x lx)
     :anchor-y       (+ center-y ly)
     :placement      :above
     :text-centered? true}))

(defmethod render! :line
  [svg mark {:keys [data encode width height tooltip point tooltip-spec] :as ctx}]
  (let [marks-g     (.select svg "g.chart g.marks")
        color-scale (get-in encode [:color :d3-scale])
        x-scale     (get-in encode [:x :d3-scale])
        y-scale     (get-in encode [:y :d3-scale])
        x-type      (get-in encode [:x :type])
        x-field     (sorting-field encode)
        value-field (get-in encode [:y :field])
        line-gen    (gen/line ctx)
        {:keys [ease-exit ease-update ease-draw
                exit-duration update-duration draw-duration]} (animation-defaults mark)
        series   (->> data
                      (prepare-data encode)
                      (mapv (fn [[k line-data]]
                              {:key    (some-> k name)
                               :color  (when color-scale (color-scale k))
                               :points line-data})))
        paths    (-> marks-g
                     (.selectAll "path.mark-line")
                     (.data (clj->js series) (fn [d] (aget d "key"))))
        entered  (-> (.enter paths)
                     (.append "path")
                     (.attr "class" (fn [d] (str "mark-line " (aget d "key"))))
                     (.attr "fill" "none")
                     (.attr "stroke-width" 2)
                     (.attr "stroke" (fn [d] (or (aget d "color") "#888")))
                     (.attr "d" (fn [d] (line-gen (aget d "points")))))]
    (trace-in! entered draw-duration ease-draw)
    (-> paths
        (.attr "stroke" (fn [d] (or (aget d "color") "#888")))
        (.transition "line-update")
        (.duration update-duration)
        (.ease ease-update)
        (.attr "d" (fn [d] (line-gen (aget d "points")))))
    (-> (.exit paths)
        (.transition)
        (.duration exit-duration)
        (.ease ease-exit)
        (.style "opacity" 0)
        (.remove))
    (when point
      (let [categories (or (get-in encode [:x :scale :domain])
                            (->> data (map x-field) (sort-by #(.getTime %)) (dedupe)))
            cat-index  (into {} (map-indexed (fn [i cat] [(.getTime cat) i]) categories))
            layers     (into-array (map (partial line-layer cat-index x-field value-field) series))]
        (markers/render!
         {:marks-g      marks-g
          :x-scale      x-scale
          :x-type       x-type
          :x-format     (get-in encode [:x :axis :format])
          :y-fn         (fn [^js layer cat-idx]
                          (y-scale (or (aget layer cat-idx) 0)))
          :value-fn     (fn [^js layer cat-idx]
                          (aget layer cat-idx))
          :color-fn     (fn [^js layer]
                          (color-scale (.-key layer)))
          :categories   categories
          :layers       layers
          :inner-width  width
          :baseline-y   height
          :tooltip      tooltip
          :tooltip-spec tooltip-spec
          :show-zero?   true})))))

(defmethod render! :bar
  [svg mark {:keys [encode] :as ctx}]
  (if (get-in encode [:y :stacked])
    (bar/render-stacked! svg mark ctx)
    (bar/render-bar! svg mark ctx)))

(defmethod render! :gauge
  [svg mark ctx]
  (gauge/render! svg mark ctx))

(defmethod render! :area
  [svg _mark {:keys [data encode width tooltip point tooltip-spec]}]
  (let [marks-g       (.select svg "g.chart g.marks")
        x-scale       (get-in encode [:x :d3-scale])
        y-scale       (get-in encode [:y :d3-scale])
        color-scale   (get-in encode [:color :d3-scale])
        x-type        (get-in encode [:x :type])
        x-field       (get-in encode [:x :field])
        categories    (or (get-in encode [:x :scale :domain])
                          (->> data (map x-field) (sort-by #(.getTime %)) (dedupe)))
        series-field  (get-in encode [:color :field])
        series-labels (->> data (map series-field) distinct)
        baseline-y    (y-scale 0)
        by-category   (group-by x-field data)
        row-data      (clj->js (prepare-area-rows categories by-category x-field))
        stack         (-> (.stack d3) (.keys (into-array (map str series-labels))))
        layers        (stack row-data)
        series-sel    (-> marks-g
                          (.selectAll "path.mark-area")
                          (.data layers (fn [^js layer] (.-key layer))))
        entered       (-> (.enter series-sel)
                          (.append "path")
                          (.attr "class" "mark-area"))
        x-mid         (fn [^js d] (x-scale (aget (.-data d) (name x-field))))
        area-gen      (gen/area x-mid y-scale)
        area-flat     (gen/area-flat x-mid baseline-y)]

    (-> (.exit series-sel)
        (.transition) (.duration 200)
        (.style "opacity" 0)
        (.remove))

    (-> entered
        (.attr "fill" (fn [^js layer] (color-scale (.-key layer))))
        (.attr "fill-opacity" 0.8)
        (.attr "d" (fn [^js layer] (area-flat layer))))

    (-> (.merge entered series-sel)
        (.attr "fill" (fn [^js layer] (color-scale (.-key layer))))
        (.attr "fill-opacity" 0.8)
        (.transition) (.duration 300) (.ease (.-easeCubicOut d3))
        (.attr "d" (fn [^js layer] (area-gen layer))))

    (when point
      (markers/render!
       {:marks-g      marks-g
        :x-scale      x-scale
        :x-type       x-type
        :x-format     (get-in encode [:x :axis :format])
        :y-fn         (fn [^js layer cat-idx]
                        (y-scale (aget (aget layer cat-idx) 1)))
        :value-fn     (fn [^js layer cat-idx]
                        (let [pt (aget layer cat-idx)]
                          (- (aget pt 1) (aget pt 0))))
        :color-fn     (fn [^js layer]
                        (color-scale (.-key layer)))
        :categories   categories
        :layers       layers
        :inner-width  width
        :baseline-y   baseline-y
        :tooltip      tooltip
        :tooltip-spec tooltip-spec}))))

(defmethod render! :arc
  [svg mark {:keys [data encode outer-radius on-bar-click tooltip
                     center-x center-y padding width height]}]
  (let [{:keys [ease-exit ease-update ease-enter
                exit-duration update-duration enter-duration]} (animation-defaults mark)
        marks-g      (.select svg "g.chart g.marks")
        theta-field  (get-in encode [:theta :field])
        color-field  (name (get-in encode [:color :field]))
        color-scale  (get-in encode [:color :d3-scale])
        arc-gen      (-> (.arc d3)
                         (.innerRadius 0)
                         (.outerRadius outer-radius))
        hover-arc    (let [r (* outer-radius 0.03)]
                       (-> (.arc d3) (.innerRadius r) (.outerRadius r)))
        cat-key      (fn [^js d] (aget (.-data d) color-field))
        arcs         (-> (gen/pie-arcs data theta-field)
                        (.filter (fn [^js d] (not= (.-startAngle d) (.-endAngle d))))
                        (gen/enforce-min-angle min-arc-angle))
        url-params-lookup (when on-bar-click
                            (into {}
                                  (keep (fn [{:keys [category url-params]}]
                                          (when url-params [category url-params])))
                                  data))
        groups       (-> marks-g
                        (.selectAll "g.arc-group")
                        (.data arcs cat-key))
        entered-g    (-> (.enter groups)
                        (.append "g")
                        (.attr "class" "arc-group"))]

    (-> entered-g
        (.append "path")
        (.attr "class" "mark-arc")
        (.style "pointer-events" "none"))

    (-> entered-g
        (.append "path")
        (.attr "class" "mark-arc-hit")
        (cond-> on-bar-click
              (.classed "clickable" true))
        (.attr "fill" "transparent"))

    (-> (.exit groups)
        (.transition) (.duration exit-duration) (.ease ease-exit)
        (.style "opacity" 0)
        (.remove))

    ;; ENTER: sweep in from a collapsed sliver at the slice's start angle
    (-> entered-g
        (.select "path.mark-arc")
        (.attr "fill" (fn [^js d] (color-scale (cat-key d))))
        (.each (fn [^js d] (this-as ^js this (set! (.-_current this) (collapsed-arc d)))))
        (sweep-arc! enter-duration ease-enter arc-gen))
    (-> entered-g
        (.select "path.mark-arc-hit")
        (.each (fn [^js d] (this-as ^js this (set! (.-_current this) (collapsed-arc d)))))
        (sweep-arc! enter-duration ease-enter arc-gen))

    ;; UPDATE: sweep existing slices to their new angles
    (-> groups
        (.select "path.mark-arc")
        (.attr "fill" (fn [^js d] (color-scale (cat-key d))))
        (sweep-arc! update-duration ease-update arc-gen))
    (-> groups
        (.select "path.mark-arc-hit")
        (sweep-arc! update-duration ease-update arc-gen))

    (let [bounds          (arc-labels/label-bounds {:padding padding :width width
                                                     :height height :center-x center-x
                                                     :center-y center-y})
          label-positions (arc-labels/calc-label-positions arcs outer-radius bounds)
          merged-g        (.merge entered-g groups)
          hits            (.select merged-g "path.mark-arc-hit")]
      (arc-labels/render-connectors! marks-g arcs outer-radius label-positions)
      (arc-labels/render-labels! marks-g arcs label-positions
                                 {:half-w (:half-w bounds) :width width})
      (-> hits
          (.on "mouseenter.hover" (hover-move! hover-arc true))
          (.on "mouseleave.hover" (hover-move! hover-arc false)))
      (when tooltip
        (let [{:keys [show! hide!]} tooltip]
          (-> marks-g
              (.selectAll "g.label-group")
              (.on "mouseenter.tooltip"
                   (fn [^js _event ^js d]
                     (show! (arc-tooltip-payload marks-g label-positions cat-key
                                                 center-x center-y d))))
              (.on "mouseleave.tooltip"
                   (fn [] (hide!))))))
      (when on-bar-click
        (.on hits "click"
             (fn [^js _event ^js d]
               (when-let [url-params (get url-params-lookup (cat-key d))]
                 (on-bar-click {:url-params url-params}))))))))
